import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import CloseIcon from "@mui/icons-material/Close";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";

import { MuiMaterials } from "../@index.js";

import { getWindowChromeThemeColor } from "./windowChrome/index.js";



const MODULES_PER_LINE = 4;

const CARD_WIDTH = 248;

const CARD_GAP = 8;

const MODULE_ROW_MIN = 88;

const POINT_CELL_GAP = 3;

const moduleCardBasis = () =>
    `calc((100% - ${(MODULES_PER_LINE - 1) * CARD_GAP}px) / ${MODULES_PER_LINE})`;

const PAPER_PADDING = 32;

const HEADER_BLOCK = 48;

const MIN_LAYOUT_WIDTH = 220;

const MIN_LAYOUT_HEIGHT = 140;



function isBanner(device) {

    return String(device?.model || "").toLowerCase() === "banner";

}

function isBrxDevice(device) {
    const text = `${device?.type || ""} ${device?.model || ""} ${device?.name || ""}`.toUpperCase();
    return text.includes("BRX") || text.includes("BX");
}

function isWeatherModule(type) {
    return type === "TMP" || type === "HUM";
}



function moduleType(module) {

    return String(module?.moduleType || "").toUpperCase();

}

function deviceHeading(device) {
    const name = String(device?.name || "").trim();
    const ip = String(device?.ipAddress || "").trim();
    if (name && ip && name !== ip) return `${name}  ${ip}`;
    return name || ip;
}

function moduleLabel(module, siblings) {
    const model = String(module?.model || "").trim();
    if (model) return model;
    const type = moduleType(module);
    const index = (siblings || []).filter((item) => moduleType(item) === type)
        .findIndex((item) => String(item.id) === String(module?.id));
    return `${type} ${index >= 0 ? index + 1 : ""}`.trim();
}



function isOutput(type) {

    return type === "DO";

}

function isInputModule(type) {
    return Boolean(type) && type !== "DO" && type !== "DI";
}



function liveRecord(module, liveById) {

    const live = liveById.get(String(module.id));

    if (live && !Array.isArray(live)) return live;

    return { points: Array.isArray(live) ? live : null, offline: false };

}



function pointValue(module, index, liveById) {

    const live = liveRecord(module, liveById);

    const points = live.points;

    if (Array.isArray(points) && index < points.length) return Number(points[index]) || 0;

    return 0;

}



function groupsFromSite(devices, modules) {

    const byIp = new Map();

    (devices || []).forEach((device) => {

        if (!device?.ipAddress || isBanner(device)) return;

        byIp.set(device.ipAddress, { device, modules: [] });

    });

    (modules || []).forEach((module) => {

        const type = moduleType(module);

        if (!type || isWeatherModule(type)) return;

        const ip = module.ipAddress || "";

        if (!byIp.has(ip)) {

            byIp.set(ip, { device: { ipAddress: ip, name: ip }, modules: [] });

        }

        byIp.get(ip).modules.push(module);

    });

    return Array.from(byIp.values())

        .map((group) => ({

            ...group,

            modules: group.modules.slice().sort((a, b) => (Number(a.slot) || 0) - (Number(b.slot) || 0)),

        }))

        .filter((group) => group.modules.length > 0);

}



function rowsOf(list, perRow) {

    const rows = [];

    const size = Math.max(1, perRow);

    for (let i = 0; i < list.length; i += size) rows.push(list.slice(i, i + size));

    return rows;

}



function inputPointColumns(count) {

    if (count >= 32) return 16;

    if (count >= 16) return 8;

    return Math.max(1, Math.ceil(count / 2));

}



function boardWidthPx() {

    return MODULES_PER_LINE * CARD_WIDTH + (MODULES_PER_LINE - 1) * CARD_GAP;

}



function initialLayoutForGroups(groups) {

    const width = boardWidthPx() + PAPER_PADDING;

    let height = HEADER_BLOCK + PAPER_PADDING;

    if (groups.length === 0) {

        height += 40;

    } else {

        groups.forEach((group, index) => {

            height += 22;

            const rowCount = rowsOf(group.modules, MODULES_PER_LINE).length;

            height += rowCount * MODULE_ROW_MIN + Math.max(0, rowCount - 1) * CARD_GAP;

            if (index < groups.length - 1) height += 10;

        });

    }

    const margin = 48;
    const layoutWidth = Math.min(window.innerWidth - margin * 2, width);
    const layoutHeight = Math.min(window.innerHeight - margin * 2, height);

    return clampLayoutToViewport({

        width: layoutWidth,

        height: layoutHeight,

        top: window.innerHeight - layoutHeight - margin,

        left: (window.innerWidth - layoutWidth) / 2,

    });

}

/** Scale input digits to cell width (container query); long values shrink instead of overlapping. */
function inputCellFontSize(value, expanded) {
    const len = Math.max(1, String(value ?? "").length);
    const maxPx = expanded ? 26 : 12;
    const minPx = expanded ? 7 : 4;
    const charWidth = 0.65;
    return `clamp(${minPx}px, calc(100cqi / ${len * charWidth}), ${maxPx}px)`;
}

function clampLayoutToViewport(layout) {
    const margin = 8;
    const maxW = Math.max(MIN_LAYOUT_WIDTH, window.innerWidth - margin * 2);
    const maxH = Math.max(MIN_LAYOUT_HEIGHT, window.innerHeight - margin * 2);
    const width = Math.min(maxW, Math.max(MIN_LAYOUT_WIDTH, layout.width));
    const height = Math.min(maxH, Math.max(MIN_LAYOUT_HEIGHT, layout.height));
    const top = Math.max(margin, Math.min(layout.top, window.innerHeight - height - margin));
    const left = Math.max(margin, Math.min(layout.left, window.innerWidth - width - margin));
    return { width, height, top, left };
}



export default function ModuleViewerDialog({
    open,
    onClose,
    devices,
    modules,
    modulePoints,
    outputsLocked,
    pendingOutputToggles = [],
    onToggleOutput,
}) {

    const dragRef = useRef(null);
    const savedLayoutRef = useRef(null);

    const groups = groupsFromSite(devices, modules);

    const layoutKey = groups.map((group) => group.modules.length).join(",");

    const [layout, setLayout] = useState(() => initialLayoutForGroups(groups));
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [focusedModuleId, setFocusedModuleId] = useState(null);
    const [fullscreen, setFullscreen] = useState(false);

    useEffect(() => {

        if (open) {
            setLayout(initialLayoutForGroups(groups));
            setDragOffset({ x: 0, y: 0 });
            setFullscreen(false);
            savedLayoutRef.current = null;
        }

    }, [open, layoutKey]);

    useEffect(() => {
        if (!open || fullscreen) return undefined;
        const onViewportChange = () => {
            setLayout((prev) => clampLayoutToViewport(prev));
        };
        window.addEventListener("resize", onViewportChange);
        return () => window.removeEventListener("resize", onViewportChange);
    }, [open, fullscreen]);

    useEffect(() => {
        if (!open) setFocusedModuleId(null);
    }, [open]);

    const exitFullscreen = useCallback(() => {
        setFullscreen(false);
        if (savedLayoutRef.current) {
            setLayout(clampLayoutToViewport(savedLayoutRef.current));
        }
    }, []);

    useEffect(() => {
        if (!open || !fullscreen) return undefined;
        const onKey = (event) => {
            if (event.key === "Escape") exitFullscreen();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, fullscreen, exitFullscreen]);

    const toggleFullscreen = useCallback((event) => {
        event?.preventDefault?.();
        event?.stopPropagation?.();
        setFullscreen((prev) => {
            if (!prev) {
                savedLayoutRef.current = layout;
                return true;
            }
            if (savedLayoutRef.current) setLayout(clampLayoutToViewport(savedLayoutRef.current));
            return false;
        });
    }, [layout]);

    const onDrag = useCallback((_event, data) => {
        setDragOffset({ x: data.x, y: data.y });
    }, []);

    const onDragStop = useCallback((_event, data) => {
        setLayout((prev) => clampLayoutToViewport({
            ...prev,
            left: prev.left + data.x,
            top: prev.top + data.y,
        }));
        setDragOffset({ x: 0, y: 0 });
    }, []);

    if (!open) return null;

    const paper = getWindowChromeThemeColor("#f8f8f8", "#1e1e1e");
    const text = getWindowChromeThemeColor("#333333", "#f3f3f3");
    const muted = getWindowChromeThemeColor("#666666", "#a8abb1");

    const fullscreenControl = (
        <MuiMaterials.IconButton
            className="module-viewer-fullscreen"
            size="small"
            aria-label={fullscreen ? "Exit full screen" : "Full screen"}
            onClick={toggleFullscreen}
            sx={{ color: text, p: 0.5, cursor: "pointer" }}
        >
            {fullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
        </MuiMaterials.IconButton>
    );

    const onResizePointerDown = (event) => {

        event.preventDefault();

        event.stopPropagation();
        if (fullscreen) exitFullscreen();

        const handle = event.currentTarget;

        const startX = event.clientX;

        const startY = event.clientY;

        const startW = layout.width;

        const startH = layout.height;

        const move = (pointerEvent) => {

            setLayout((prev) => clampLayoutToViewport({

                ...prev,

                width: Math.max(MIN_LAYOUT_WIDTH, startW + (pointerEvent.clientX - startX)),

                height: Math.max(MIN_LAYOUT_HEIGHT, startH + (pointerEvent.clientY - startY)),

            }));

        };

        const up = () => {

            handle.removeEventListener("pointermove", move);

            handle.removeEventListener("pointerup", up);

        };

        handle.setPointerCapture(event.pointerId);

        handle.addEventListener("pointermove", move);

        handle.addEventListener("pointerup", up);

    };



    const liveById = new Map((modulePoints || []).map((item) => [String(item.id), item]));

    const flat = groups.flatMap((group) => group.modules);

    const toggleInputFocus = (module) => {
        const type = moduleType(module);
        if (!isInputModule(type)) return;
        const id = String(module.id);
        setFocusedModuleId((current) => (current === id ? null : id));
    };

    const renderPointsCentered = (grid) => (
        <MuiMaterials.Box
            sx={{
                flex: 1,
                minHeight: 0,
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                px: 0.25,
                py: 0.25,
            }}
        >
            {grid}
        </MuiMaterials.Box>
    );

    const renderPoints = (module, expanded = false, brx = false) => {

        const type = moduleType(module);

        const count = Math.max(0, Number(module.dataLength) || 0);

        const live = liveRecord(module, liveById);

        const sourceCount = Math.max(count, Array.isArray(live.points) ? live.points.length : 0);

        if (isOutput(type)) {
            const outputCount = Math.min(
                16,
                Math.max(
                    Number(module.dataLength) || 0,
                    Array.isArray(live.points) ? live.points.length : 0,
                ) || 16,
            );
            const slots = Array.from({ length: outputCount }, (_, index) => ({
                value: pointValue(module, index, liveById),
                index,
            }));
            return renderPointsCentered(
                <MuiMaterials.Box
                    sx={{
                        width: "100%",
                        display: "grid",
                        gridTemplateColumns: `repeat(${Math.min(8, outputCount)}, minmax(0, 1fr))`,
                        gap: `${POINT_CELL_GAP}px`,
                    }}
                >
                    {slots.map((point) => {
                        const pendingEntry = (pendingOutputToggles || []).find(
                            (p) =>
                                Number(p.moduleId) === Number(module.id) &&
                                Number(p.index) === Number(point.index),
                        );
                        const outputPending = Boolean(pendingEntry);
                        const displayOn = outputPending
                            ? pendingEntry.targetOn != null
                                ? pendingEntry.targetOn
                                : Boolean(point.value)
                            : Boolean(point.value);
                        return (
                        <MuiMaterials.Box
                            key={`${module.id}-${point.index}`}
                            sx={{
                                aspectRatio: "1",
                                minWidth: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <MuiMaterials.Box
                                title={
                                    outputPending
                                        ? `${point.index}: turning ${displayOn ? "on" : "off"}…`
                                        : `${point.index}: ${point.value ? "on" : "off"}`
                                }
                                onClick={(event) => {
                                    if (outputsLocked || !onToggleOutput || outputPending) return;
                                    event.stopPropagation();
                                    onToggleOutput(module.id, point.index);
                                }}
                                sx={{
                                    position: "relative",
                                    width: "76%",
                                    height: "76%",
                                    borderRadius: "50%",
                                    bgcolor: displayOn ? "#e10600" : "#c0c0c0",
                                    cursor:
                                        outputsLocked || !onToggleOutput || outputPending
                                            ? "default"
                                            : "pointer",
                                    pointerEvents: "auto",
                                }}
                            >
                                {outputPending ? (
                                    <MuiMaterials.Box
                                        sx={{
                                            position: "absolute",
                                            inset: 0,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            borderRadius: "inherit",
                                            bgcolor: "rgba(0, 0, 0, 0.28)",
                                        }}
                                    >
                                        <MuiMaterials.CircularProgress
                                            thickness={4}
                                            sx={{
                                                color: "#f3f3f3",
                                                width: "58% !important",
                                                height: "58% !important",
                                            }}
                                        />
                                    </MuiMaterials.Box>
                                ) : null}
                            </MuiMaterials.Box>
                        </MuiMaterials.Box>
                        );
                    })}
                </MuiMaterials.Box>,
            );

        }

        const dl = Math.max(0, Number(module.dataLength) || 0);
        const pointCount = dl > 0 ? dl : sourceCount || 16;

        const indexes = brx || pointCount < 32

            ? Array.from({ length: pointCount }, (_, index) => index)

            : Array.from({ length: 16 }, (_, index) => index * 2);

        const columns = indexes.length >= 16 ? 8 : inputPointColumns(indexes.length);

        const cells = indexes.map((index) => ({
            value: pointValue(module, index, liveById),
            index,
        }));

        const renderInputCell = (point, cellKey) => (
            <MuiMaterials.Box
                key={cellKey ?? `${module.id}-${point.index}`}
                sx={{
                    minWidth: 0,
                    width: "100%",
                    height: "100%",
                    maxWidth: "100%",
                    overflow: "hidden",
                    containerType: "inline-size",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    px: 0.125,
                }}
            >
                <MuiMaterials.Typography
                    component="span"
                    sx={{
                        fontSize: inputCellFontSize(point.value, expanded),
                        lineHeight: 1,
                        textAlign: "center",
                        color: "#f3f3f3",
                        fontVariantNumeric: "tabular-nums",
                        whiteSpace: "nowrap",
                        maxWidth: "100%",
                    }}
                >
                    {point.value}
                </MuiMaterials.Typography>
            </MuiMaterials.Box>
        );

        const inputRowGrid = (rowCells, rowKey) => (
            <MuiMaterials.Box
                key={rowKey}
                sx={{
                    display: "grid",
                    gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
                    gap: `${POINT_CELL_GAP}px`,
                    flex: "0 0 auto",
                    width: "100%",
                    alignItems: "center",
                }}
            >
                {rowCells.map((point) =>
                    renderInputCell(point, `${module.id}-${rowKey}-${point.index}`),
                )}
            </MuiMaterials.Box>
        );

        if (expanded) {
            const pointRows = rowsOf(cells, columns);
            return (
                <MuiMaterials.Box
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-evenly",
                        alignItems: "stretch",
                        px: 1.5,
                    }}
                >
                    {pointRows.map((rowCells, rowIndex) =>
                        inputRowGrid(rowCells, `${module.id}-expanded-row-${rowIndex}`),
                    )}
                </MuiMaterials.Box>
            );
        }

        const gridColumns = columns >= 8 ? 8 : columns;

        return renderPointsCentered(
            <MuiMaterials.Box
                sx={{
                    width: "100%",
                    display: "grid",
                    gridTemplateColumns: `repeat(${gridColumns}, minmax(0, 1fr))`,
                    gap: `${POINT_CELL_GAP}px`,
                }}
            >
                {cells.map((point) => (
                    <MuiMaterials.Box
                        key={`${module.id}-${point.index}`}
                        sx={{
                            aspectRatio: `${Math.max(1, 8 / gridColumns)}`,
                            minWidth: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        {renderInputCell(point, `${module.id}-${point.index}`)}
                    </MuiMaterials.Box>
                ))}
            </MuiMaterials.Box>,
        );

    };



    const paperShellSx = {
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        bgcolor: paper,
        color: text,
        border: "1px solid #000",
        boxSizing: "border-box",
    };

    const viewerBody = (
                <>

                    <MuiMaterials.Box
                        className="module-viewer-drag"
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            mb: 1,
                            flexShrink: 0,
                            minHeight: 32,
                            cursor: fullscreen ? "default" : "move",
                        }}
                    >

                        {focusedModuleId ? (
                            <MuiMaterials.Button
                                className="module-viewer-cancel-focus"
                                size="small"
                                variant="outlined"
                                onClick={() => setFocusedModuleId(null)}
                                sx={{ mr: 1, minWidth: 64, py: 0.25, fontSize: 12, textTransform: "none" }}
                            >
                                Cancel
                            </MuiMaterials.Button>
                        ) : null}

                        {fullscreenControl}

                        <MuiMaterials.IconButton

                            className="module-viewer-close"

                            size="small"

                            aria-label="Close"

                            onClick={onClose}

                            sx={{ color: text, p: 0.5, cursor: "pointer" }}

                        >

                            <CloseIcon fontSize="small" />

                        </MuiMaterials.IconButton>

                    </MuiMaterials.Box>

                    {flat.length === 0 ? (

                        <MuiMaterials.Typography sx={{ color: muted, fontSize: 13, textAlign: "center" }}>

                            No modules on this site.

                        </MuiMaterials.Typography>

                    ) : focusedModuleId ? (() => {
                        const focused = flat.find((module) => String(module.id) === focusedModuleId);
                        if (!focused) return null;
                        const group = groups.find((g) => g.modules.some((m) => String(m.id) === focusedModuleId));
                        const live = liveRecord(focused, liveById);
                        const offline = Boolean(live.offline);
                        return (
                            <MuiMaterials.Box
                                className="module-viewer-input-card"
                                onClick={() => toggleInputFocus(focused)}
                                sx={{
                                    flex: 1,
                                    minHeight: 0,
                                    display: "flex",
                                    flexDirection: "column",
                                    bgcolor: "#454545",
                                    border: "1px solid #000",
                                    borderRadius: "4px",
                                    px: 1.5,
                                    py: 1,
                                    color: "#f3f3f3",
                                    cursor: "pointer",
                                }}
                            >
                                <MuiMaterials.Typography sx={{ color: muted, fontSize: 13, mb: 0.5, flexShrink: 0 }}>
                                    {deviceHeading(group?.device)}
                                </MuiMaterials.Typography>
                                <MuiMaterials.Typography sx={{ fontSize: 16, textAlign: "center", fontWeight: 600, mb: 1, flexShrink: 0 }}>
                                    {moduleLabel(focused, group?.modules || [])}
                                </MuiMaterials.Typography>
                                {offline ? (
                                    <MuiMaterials.Typography sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, color: "#e10600" }}>
                                        offline
                                    </MuiMaterials.Typography>
                                ) : renderPoints(focused, true, isBrxDevice(group?.device))}
                            </MuiMaterials.Box>
                        );
                    })() : (

                        <MuiMaterials.Box sx={{ flex: 1, minHeight: 0, overflow: "auto", display: "flex", flexDirection: "column", gap: 1.5, scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" }, "& > :first-of-type": { mt: "auto" }, "& > :last-of-type": { mb: "auto" } }}>

                            {groups.map((group) => (

                                <MuiMaterials.Box key={group.device.ipAddress || group.device.name} sx={{ flex: "0 0 auto", display: "flex", flexDirection: "column" }}>

                                    <MuiMaterials.Typography sx={{ color: muted, fontSize: 13, mb: 0.5, flexShrink: 0 }}>

                                        {deviceHeading(group.device)}

                                    </MuiMaterials.Typography>

                                    <MuiMaterials.Box sx={{ display: "flex", flexDirection: "column", gap: `${CARD_GAP}px`, alignItems: "center" }}>

                                        {rowsOf(group.modules, MODULES_PER_LINE).map((row, rowIndex) => (

                                            <MuiMaterials.Box

                                                key={`${group.device.ipAddress}-${rowIndex}`}

                                                sx={{

                                                    width: "100%",

                                                    flex: "0 0 auto",

                                                    display: "flex",

                                                    flexWrap: "wrap",

                                                    justifyContent: "center",

                                                    gap: `${CARD_GAP}px`,

                                                }}

                                            >

                                                {row.map((module) => {

                                                    const live = liveRecord(module, liveById);

                                                    const offline = Boolean(live.offline);

                                                    const inputModule = isInputModule(moduleType(module));
                                                    const cardBasis = moduleCardBasis();
                                                    return (

                                                    <MuiMaterials.Box
                                                        key={module.id}
                                                        className={inputModule ? "module-viewer-input-card" : undefined}
                                                        onClick={inputModule ? () => toggleInputFocus(module) : undefined}
                                                        sx={{
                                                            flex: `0 1 ${cardBasis}`,
                                                            width: cardBasis,
                                                            maxWidth: cardBasis,
                                                            minWidth: 0,
                                                            minHeight: MODULE_ROW_MIN,
                                                            boxSizing: "border-box",
                                                            display: "flex",
                                                            flexDirection: "column",
                                                            bgcolor: "#454545",
                                                            border: "1px solid #000",
                                                            borderRadius: "4px",
                                                            px: 0.75,
                                                            py: 0.5,
                                                            color: "#f3f3f3",
                                                            cursor: inputModule ? "pointer" : "default",
                                                        }}
                                                    >

                                                        <MuiMaterials.Typography sx={{ fontSize: 12, textAlign: "center", lineHeight: 1.2, flexShrink: 0 }}>

                                                            {moduleLabel(module, group.modules)}

                                                        </MuiMaterials.Typography>

                                                        {offline ? (

                                                            <MuiMaterials.Typography sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center", fontSize: 13, color: "#e10600", minHeight: 36 }}>

                                                                offline

                                                            </MuiMaterials.Typography>

                                                        ) : renderPoints(module, false, isBrxDevice(group.device))}

                                                    </MuiMaterials.Box>

                                                    );

                                                })}

                                            </MuiMaterials.Box>

                                        ))}

                                    </MuiMaterials.Box>

                                </MuiMaterials.Box>

                            ))}

                        </MuiMaterials.Box>

                    )}

                </>
    );

    if (fullscreen) {
        return createPortal(
            <MuiMaterials.Paper
                sx={{
                    ...paperShellSx,
                    position: "fixed",
                    inset: 0,
                    zIndex: 2100,
                    width: "100%",
                    height: "100%",
                    maxHeight: "100dvh",
                    borderRadius: 0,
                    boxShadow: "none",
                    p: 0,
                    pt: "max(12px, env(safe-area-inset-top, 0px))",
                    pb: "max(12px, env(safe-area-inset-bottom, 0px))",
                    pl: "max(12px, env(safe-area-inset-left, 0px))",
                    pr: "max(12px, env(safe-area-inset-right, 0px))",
                }}
            >
                {viewerBody}
            </MuiMaterials.Paper>,
            document.body,
        );
    }

    return (

        <MuiMaterials.Box sx={{ position: "fixed", inset: 0, zIndex: 1400, pointerEvents: "none" }}>

            <MuiMaterials.Draggable
                nodeRef={dragRef}
                handle=".module-viewer-drag"
                cancel=".module-viewer-close, .module-viewer-resize, .module-viewer-fullscreen, .module-viewer-cancel-focus, .module-viewer-input-card"
                position={dragOffset}
                onDrag={onDrag}
                onStop={onDragStop}
            >

                <MuiMaterials.Paper
                    ref={dragRef}
                    sx={{
                        ...paperShellSx,
                        position: "absolute",
                        top: layout.top,
                        left: layout.left,
                        width: layout.width,
                        height: layout.height,
                        borderRadius: "12px",
                        boxShadow: "0 8px 28px rgba(0,0,0,0.45)",
                        p: 2,
                        pointerEvents: "auto",
                    }}
                >
                    {viewerBody}
                    <MuiMaterials.Box
                        className="module-viewer-resize"
                        onPointerDown={onResizePointerDown}
                        sx={{
                            position: "absolute",
                            right: 0,
                            bottom: 0,
                            width: 16,
                            height: 16,
                            cursor: "nwse-resize",
                            touchAction: "none",
                        }}
                    />
                </MuiMaterials.Paper>

            </MuiMaterials.Draggable>

        </MuiMaterials.Box>

    );

}


